import React, {
  useEffect,
  useState,
} from "react";

import { createPortal } from "react-dom";

import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "../../../components/Modal/Modal";

import {
  FaCalendarPlus,
  FaExpand,
  FaMapMarkedAlt,
  FaTimes,
} from "react-icons/fa";

import EventCard from "./EventCard";

import {
  downloadEventIcs,
  getGoogleMapsUrl,
} from "./utils/calendarViewUtils";

/* ========================================================================== */
/*                                DATE LABEL                                  */
/* ========================================================================== */

function getDateLabel(event) {
  const start = event?.start
    ? new Date(event.start)
    : null;

  const end = event?.end
    ? new Date(event.end)
    : null;

  if (
    !start ||
    Number.isNaN(start.getTime())
  ) {
    return "";
  }

  const startLabel =
    start.toLocaleDateString("fr-FR", {
      timeZone: "Europe/Paris",
    });

  if (
    !end ||
    Number.isNaN(end.getTime())
  ) {
    return startLabel;
  }

  const endLabel =
    end.toLocaleDateString("fr-FR", {
      timeZone: "Europe/Paris",
    });

  return startLabel === endLabel
    ? startLabel
    : `${startLabel} → ${endLabel}`;
}

function EventImageLightbox({
  isOpen,
  imageUrl,
  title,
  onClose,
}) {
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [isOpen, onClose]);

  if (
    !isOpen ||
    !imageUrl ||
    typeof document === "undefined"
  ) {
    return null;
  }

  return createPortal(
    <div
      className="
        fixed inset-0
        z-[99999]
        flex items-center justify-center
        bg-black/95
        p-3
        backdrop-blur-sm
        sm:p-6
      "
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={
        title
          ? `Agrandissement de ${title}`
          : "Agrandissement de l’image"
      }
    >
      {/* ================================================================ */}
      {/* BOUTON FERMETURE                                                 */}
      {/* ================================================================ */}

      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onClose();
        }}
        className="
          absolute
          right-3 top-3
          z-[100000]
          flex h-12 w-12
          items-center justify-center
          rounded-full
          bg-white
          text-xl
          text-slate-900
          shadow-2xl
          transition
          hover:scale-105
          hover:bg-slate-100
          sm:right-6 sm:top-6
        "
        aria-label="Fermer l’image"
        title="Fermer"
      >
        <FaTimes />
      </button>

      {/* ================================================================ */}
      {/* INDICATION DESKTOP                                               */}
      {/* ================================================================ */}

      <div
        className="
          pointer-events-none
          absolute
          left-6 top-6
          hidden
          items-center gap-2
          rounded-full
          bg-black/60
          px-3 py-2
          text-xs font-semibold
          text-white
          backdrop-blur
          sm:flex
        "
      >
        <FaExpand />

        <span>
          Échap ou clic en dehors pour fermer
        </span>
      </div>

      {/* ================================================================ */}
      {/* IMAGE                                                            */}
      {/* ================================================================ */}

      <div
        className="
          flex
          h-full w-full
          items-center justify-center
        "
        onClick={(event) =>
          event.stopPropagation()
        }
      >
        <img
          src={imageUrl}
          alt={
            title ||
            "Illustration de l’événement"
          }
          className="
            max-h-[calc(100vh-2rem)]
            max-w-[calc(100vw-2rem)]
            select-none
            object-contain
            sm:max-h-[calc(100vh-3rem)]
            sm:max-w-[calc(100vw-3rem)]
          "
          draggable={false}
        />
      </div>

      {/* ================================================================ */}
      {/* TITRE                                                            */}
      {/* ================================================================ */}

      {title && (
        <div
          className="
            pointer-events-none
            absolute
            bottom-4 left-1/2
            max-w-[90vw]
            -translate-x-1/2
            rounded-xl
            bg-black/60
            px-4 py-2
            text-center
            text-sm font-semibold
            text-white
            backdrop-blur
          "
        >
          {title}
        </div>
      )}
    </div>,
    document.body
  );
}
/* ========================================================================== */
/*                            EVENT DETAILS MODAL                             */
/* ========================================================================== */

export default function EventDetailsModal({
  isOpen,
  onClose,
  event,
}) {
  const [
    isImageOpen,
    setIsImageOpen,
  ] = useState(false);

  /* ---------------------------------------------------------------------- */
  /* Fermeture lightbox quand événement / modale change                     */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    if (!isOpen) {
      setIsImageOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    setIsImageOpen(false);
  }, [event?.id]);

  if (!event) {
    return null;
  }

  const dateLabel =
    getDateLabel(event);

  const mapsUrl =
    getGoogleMapsUrl(
      event.lieu_event
    );

  const subtitle = [
    dateLabel,
    event.discipline_event,
    event.lieu_event,
  ]
    .filter(Boolean)
    .join(" • ");

  return (
    <>
      {/* ================================================================== */}
      {/* MODALE DÉTAIL                                                     */}
      {/* ================================================================== */}

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
        labelledById="event-modal-title"
      >
        <ModalHeader
          id="event-modal-title"
          title={
            event.titre ||
            "Détail de l’événement"
          }
          subtitle={subtitle}
          onClose={onClose}
        />

        <ModalBody>
          <EventCard
            event={event}
            detailed
            onImageClick={
              event.photo_url
                ? () =>
                    setIsImageOpen(
                      true
                    )
                : undefined
            }
          />

          {event.photo_url && (
            <div className="mt-3 flex justify-center">
              <button
                type="button"
                onClick={() =>
                  setIsImageOpen(true)
                }
                className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-bold text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-white"
              >
                <FaExpand />

                Cliquez sur l’image
                pour l’agrandir
              </button>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-white/10"
            >
              <FaTimes />

              Fermer
            </button>

            {mapsUrl && (
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 text-sm font-bold text-blue-700 transition hover:bg-blue-100 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-950/50"
              >
                <FaMapMarkedAlt />

                Itinéraire
              </a>
            )}

            <button
              type="button"
              onClick={() =>
                downloadEventIcs(
                  event
                )
              }
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-red-600 px-4 text-sm font-black text-white shadow-lg shadow-red-600/20 transition hover:bg-red-700"
            >
              <FaCalendarPlus />

              Ajouter au calendrier
            </button>
          </div>
        </ModalFooter>
      </Modal>

      {/* ================================================================== */}
      {/* LIGHTBOX IMAGE                                                    */}
      {/* ================================================================== */}

      <EventImageLightbox
        isOpen={isImageOpen}
        imageUrl={event.photo_url}
        title={event.titre}
        onClose={() =>
          setIsImageOpen(false)
        }
      />
    </>
  );
}